// Split out of src/components/ChildView.jsx (docs/FILE_SPLIT_RUN1_SPEC.md, move-only). ChildView.jsx
// re-exports everything it exported before, so existing imports keep working.

import { TopGoalCard } from "../ChildHero.jsx";
import { attributionName } from "../../lib/adults.js";
import { ActivityList, ActivityRow, CoinIcon, Icon, Piggington, VaultIcon, characterName } from "../Common.jsx";
import { ArcadeCard } from "../SnackCatch.jsx";
import { AskToBuyCard, AskToBuySheet } from "../AskToBuy.jsx";
import { openAsk } from "../../lib/askToBuy.js";
import { kidTimeline } from "../../lib/kidRequests.js";
import { ACTIVITY_KIND_STYLE, uid } from "../../lib/constants.js";
import { oinksToGo, nextCharacterFor } from "../../lib/oinks.js";
import { t as ct } from "../../lib/content.js";
import { useBackLayer } from "../../lib/backstack.js";
import { activityRowStyle, fmtActivityStamp, fmtNumber, fmtPence, habitsForChild, isHabitDayComplete, pickTopGoal, todayStr } from "../../lib/utils.js";
import { DoubleTokenDayBanner, CollapsibleSection, TrophiesSection } from "./kidShared.jsx";
import { PigCard } from "./OinkShop.jsx";
import { DailyQuestionCard } from "./DailyQuestion.jsx";
import { StreakCard } from "./EarnTab.jsx";
import { useGoalEditor, WishListPair } from "./RewardsTab.jsx";
import { LearnEarnTeaser } from "./LearnTab.jsx";

const { useState, useEffect, useCallback, useMemo, useRef, useId } = React;

/* --------------------------- Home tab (kid) --------------------------- */


// Journey review P2#1 — one-time orientation card on a kid's first Home visit. Device-
// scoped on purpose (localStorage, not household state): seeing it once per device is
// harmless-to-useful, and it keeps the state schema untouched (no preview seeding, no
// migration). Sprite icons per the proper-icons rule.
// LB-244: the dismissed flag lives in HomeTab (useKidWelcomeDismissed) so Home can leave the
// whole wrapper out when there's nothing to show, instead of rendering an empty box.
export function kidWelcomeKey(child) { return `lb_kid_welcome_${child.id}`; }
export function useKidWelcomeDismissed(child) {
  return useState(() => {
    try { return localStorage.getItem(kidWelcomeKey(child)) === "1"; } catch { return true; }
  });
}
function KidWelcomeCard({ child, dismissed, setDismissed }) {
  const storageKey = kidWelcomeKey(child);
  if (dismissed) return null;
  function dismiss() {
    try { localStorage.setItem(storageKey, "1"); } catch {}
    setDismissed(true);
  }
  // V.1 (Alan): on a wide screen the block uses its width — Piggington larger, the three
  // lines side by side as small tiles, "Got it" on the right. A phone keeps the stack.
  const line = (iconId, boldText, rest) => (
    <p className="text-xs text-slate-600 flex items-center gap-1.5 mb-1 md:mb-0 md:flex-1 md:items-start md:gap-2 md:text-xs md:leading-snug">
      <Icon id={iconId} size={16} className="flex-shrink-0 md:mt-0.5" style={{ color: "currentColor" }} />
      <span><b>{boldText}</b> {rest}</span>
    </p>
  );
  return (
    <div className="kc-pig flex gap-3 items-start pt-0 pb-2 px-0.5 md:items-center md:gap-5 md:py-3" data-testid="kid-welcome">
      <Piggington expression="fx-happy" className="h-14 w-auto flex-shrink-0 md:h-20" />
      <div className="min-w-0 md:flex-1">
      <div className="text-sm font-extrabold kc-title mb-1 md:text-base md:mb-2">{ct("kid.welcome.heading", "Hi {name}, welcome to Little Bankers", { name: child.name })}</div>
      <div className="md:flex md:gap-6">
        {line("ic-medal", ct("kid.welcome.earn_bold", "Earn"), ct("kid.welcome.earn_rest", "tokens for tasks and games"))}
        {line("ic-goals", ct("kid.welcome.save_bold", "Save"), ct("kid.welcome.save_rest", "them for goals you pick"))}
        {line("ic-till", ct("kid.welcome.spend_bold", "Spend"), ct("kid.welcome.spend_rest", "them when your grown-up says yes"))}
      </div>
      <p className="text-right mt-1.5 md:hidden">
        <button className="text-xs font-bold text-slate-900 underline underline-offset-2" data-testid="kid-welcome-dismiss" onClick={dismiss}>
          {ct("kid.welcome.dismiss", "Got it")}
        </button>
      </p>
      </div>
      <button className="hidden md:inline-block flex-shrink-0 self-start bg-pill text-navy text-xs font-bold rounded-lg px-3 py-2" data-testid="kid-welcome-dismiss-wide" onClick={dismiss}>
        {ct("kid.welcome.dismiss", "Got it")}
      </button>
    </div>
  );
}

export function HomeTab({ code, child, adults = [], requests, transactions, habits, habitLog, onToggleHabit, onRedeemGoal, rewardSettings, kidTheme, learning, onOpenEarn, onOpenLearnEarn, onOpenRewards, oinksOn, onOpenOinkShop, onOpenArcade, onWithdrawRequest, onAnswerDailyQuestion, badgeCelebrate, perfectWeekCelebrate, capBlocked, onChangeAvatar, onUpdateGoals = null, onAskToBuy = null }) {
  const goals = child.goals || [];
  const topGoal = pickTopGoal(goals, child, rewardSettings?.exchangeRate);
  // LB-385 (Alan, 30 Sep): with no goal, Home carries the wish list — the same section, card and
  // two buttons as Rewards, opening the same goal form and the same ask sheet. It takes the top
  // goal's place (order-4, after Today and Play: Home still leads with earning, 25 Aug) and
  // goes the moment a goal exists, when the top goal card is back.
  const rate = rewardSettings?.exchangeRate > 0 ? rewardSettings.exchangeRate : 10;
  const [askOpen, setAskOpen] = useState(false);
  useBackLayer(askOpen, () => setAskOpen(false)); // LB-315
  const { startAddGoal, modal: goalModal } = useGoalEditor({ code, goals, rate, onUpdateGoals: onUpdateGoals || (() => {}), kidTheme });
  const showWishList = goals.length === 0 && !!onUpdateGoals;
  const waitingAsk = onAskToBuy ? openAsk(requests, child.id) : null;
  // Staging feedback (Aug 2026), Design A order: daily question and daily tasks lead (the
  // kid wants newness), then Learn & Earn (what the app most wants driven), then the
  // reflective/secondary cards. Once today's habits are all done, the Daily Streak card
  // disappears from Home entirely — its job here is "come do your tasks", which is finished
  // for the day — but the full task list stays fully available on the Earn tab exactly as
  // before (DailyTasksSection there is a separate component, untouched by this).
  const todayHabitsDone = habitsForChild(habits, child.id).length > 0 && isHabitDayComplete(habitLog, habits, child.id, todayStr());
  // LB-164 (Alan, 6 Sep — approved sketch): Home is sections and rows on the canvas; only
  // taps are cards (Daily Question, Brain boost + Arcade as a half-width pair, the top goal,
  // Piggington). Section headings carry the family disc. On a phone the two column wrappers
  // are `contents`, so the children flow in their `order-*`; from the tablet breakpoint they
  // become real columns — hero/Today/Piggington left, Play/My stuff/Recent activity right.
  // LB-244 (Alan, 8 Sep, second screenshot): a section with nothing to show renders no
  // wrapper at all, and the first section that IS on screen takes the tight top margin —
  // before this, a dismissed welcome and a finished Today each left an empty box that still
  // cost a flex gap, and Play kept a full section gap under the hero.
  const [welcomeDismissed, setWelcomeDismissed] = useKidWelcomeDismissed(child);
  const qDone = !oinksOn || (learning?.dailyQ && learning.dailyQ.date === todayStr());
  const todayEmpty = qDone && todayHabitsDone;
  const firstSection = !welcomeDismissed ? "welcome" : !todayEmpty ? "today" : "play";
  return (
    // White sheet (Alan, 9 Sep — kid-mode design review, drawn + signed off): one white
    // surface, hairlines between blocks, no section headers on Home — each block's own title
    // is the header. Play keeps a small title because its two tiles have none of their own.
    // The streak block is the full StreakCard with today's tasks, so a child ticks from Home
    // (Alan, item 5) instead of tapping through to Earn.
    <div className="kid-home kid-sheet flex flex-col">
      <div className="contents">
        <div className="order-1 px-4"><DoubleTokenDayBanner child={child} settings={rewardSettings} /></div>
        {!welcomeDismissed && <div className="order-1"><KidWelcomeCard child={child} dismissed={welcomeDismissed} setDismissed={setWelcomeDismissed} /></div>}
        {!todayEmpty && <div className="order-1">
          {/* Alan (7 Sep): once the question is answered and today's tasks are done there is
              nothing left in Today — the block and its spacing go, not just its contents. */}
          <>
              <DailyQuestionCard child={child} learning={learning} oinksOn={oinksOn} onAnswer={onAnswerDailyQuestion} rewardSettings={rewardSettings} />
              {!todayHabitsDone && <StreakCard child={child} habits={habits} habitLog={habitLog} requests={requests} onToggleHabit={onToggleHabit} showTasks={true} code={code} settings={rewardSettings} />}
          </>
        </div>}
        <div className="order-5">
          <PigCard child={child} habits={habits} habitLog={habitLog} oinksOn={oinksOn} onOpenShop={onOpenOinkShop} badgeCelebrate={badgeCelebrate} perfectWeekCelebrate={perfectWeekCelebrate} capBlocked={capBlocked} topGoal={topGoal} rewardSettings={rewardSettings} onChangeAvatar={onChangeAvatar} />
          {/* LB-168: the aspiration hook on the screen they see most — the next character and
              the distance to it, as one quiet row. Hidden when Oinks are off or nothing is left to earn. */}
          {oinksOn && (() => { const nx = nextCharacterFor(child); const d = nx ? oinksToGo(child, nx.price) : null; return nx ? (
            <button onClick={onOpenOinkShop} className="kid-rows ks-line w-full text-left mt-2" data-testid="next-character-row">
              <span className="kid-row"><span className="flex items-center gap-2 text-sm text-slate-700"><Piggington expression="fx-neutral" base={nx.id} className="h-6 w-auto" />{d ? ct("home.next_character", "Next: {name}, {n} Oinks to go", { name: characterName(nx.id), n: d.toGo }) : ct("home.next_character_ready", "{name} is ready to buy in the Oink Shop", { name: characterName(nx.id) })}</span><Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} /></span>
            </button>
          ) : null; })()}
        </div>
      </div>
      <div className="contents">
        <div className="order-3 ks-block kc-play">
          <div className="ks-title kc-play"><span className="kc-disc" aria-hidden="true"><Icon id="ic-arcade" size={16} style={{ color: "currentColor" }} /></span><span className="kc-title">{ct("kid.home.section_play", "Play")}</span></div>
          <div className="grid grid-cols-2 gap-2">
            <LearnEarnTeaser learning={learning || {}} onOpen={onOpenLearnEarn} />
            {/* R5: not gated by oinksOn — the game only borrows the pig art; the parent's
                Arcade toggle inside ArcadeCard is its only gate. */}
            <ArcadeCard child={child} learning={learning || {}} onOpen={onOpenArcade} />
          </div>
        </div>
        <div className="order-4">
          {showWishList ? (
            <div data-testid="home-wish-list">
              <CollapsibleSection title={ct("kid.rewards.section_goals", "My wish list")} tone="amber" icon="ic-goals" variant="section">
                <WishListPair hasGoals={false} onSaveUp={startAddGoal} onBuyNow={onAskToBuy ? () => setAskOpen(true) : null} buyWaiting={!!waitingAsk} />
                {waitingAsk && <AskToBuyCard child={child} requests={requests} rate={rate} adultName={adults.length === 1 ? (adults[0].name || "") : ""} onOpen={() => setAskOpen(true)} />}
              </CollapsibleSection>
              {goalModal}
              {askOpen && (
                <AskToBuySheet child={child} code={code} rate={rate} onSend={onAskToBuy} onMakeGoal={(goal) => onUpdateGoals([...goals, { id: uid(), ...goal }])} onClose={() => setAskOpen(false)} />
              )}
            </div>
          ) : (
            <TopGoalCard goal={topGoal} child={child} requests={requests} onRedeemGoal={onRedeemGoal} rewardSettings={rewardSettings} kidTheme={kidTheme} onOpenRewards={onOpenRewards} code={code} />
          )}
          <TrophiesSection code={code} child={child} learning={learning} />
        </div>
        <div className="order-6 ks-last">{/* visually last on the sheet: no trailing hairline */}
          {/* LB-346 (Alan, 30 Sep, option R): one list — waiting requests pinned on top, then
              the timeline with refusals in it. */}
          <RecentActivityCard transactions={transactions} requests={requests} adults={adults} onWithdraw={onWithdrawRequest} />
        </div>
      </div>
    </div>
  );
}

// LB-346 (Alan, 30 Sep, option R signed off): a request row inside Recent activity. Waiting
// rows are pinned above the timeline (a tinted band with its own caption, because they are NOT
// in date order with what follows); a refusal sits in the timeline as an UNNAMED "Not this time"
// with the parent's note when they wrote one; "Taken back" and "Made a goal" likewise. An
// approved request has no row of its own: its transaction is already here, named (LB-343).
//
// LAYOUT RULE (Alan: "lots of issues in the transaction lists where new additions have caused
// wrapping"): each row is two columns and only two — a min-w-0 text column that clips, and a
// chip that never shrinks and never wraps. Nothing else sits in the row, so a long title, a
// long adult name or a long note can never push the chip onto a second line: the title
// ellipsises, the chip caps its own width and ellipsises, the note is a full-width line of its
// own under the row. Withdraw is inside the text column for the same reason.
function KidRequestRow({ row, onWithdraw }) {
  const chip =
    row.state === "waiting" ? { cls: "bg-amber-100 text-amber-800", text: ct("kid.requests.chip_waiting", "Waiting") }
    : row.state === "no" ? { cls: "bg-slate-100 text-slate-600", text: ct("kid.requests.chip_no", "Not this time") }
    : row.state === "goal" ? { cls: "bg-slate-50 text-slate-600", text: ct("kid.requests.chip_goal", "Made a goal") }
    : { cls: "bg-slate-50 text-slate-600", text: ct("kid.requests.chip_back", "Taken back") };
  const stamp = fmtActivityStamp(row.at);
  const amount = row.pricePence != null ? fmtPence(row.pricePence) : row.tokens > 0 ? ct("kid.requests.tokens", "{n} tokens", { n: row.tokens }) : "";
  const sub = [stamp, row.state === "waiting" ? amount : (amount ? ct("kid.requests.asked_for", "you asked for {amount}", { amount }) : "")].filter(Boolean).join(" · ");
  return (
    <div className={`kid-row${row.state === "waiting" ? " bg-amber-50/60" : ""}`} data-testid="kid-request-row" data-state={row.state}>
      <div className="min-w-0 flex-1 flex flex-col gap-0.5 text-left">
        <div className="text-[15px] font-bold text-slate-800 truncate" data-testid="kid-request-title">{row.title}</div>
        {sub && <div className="text-xs font-semibold text-slate-500 truncate">{sub}</div>}
        {row.note && <div className="text-xs font-semibold text-slate-600 mt-1 rounded-lg bg-slate-50 px-2.5 py-2 break-words" data-testid="kid-request-note">{row.note}</div>}
        {row.canWithdraw && onWithdraw && (
          <button type="button" className="self-start text-xs font-bold text-rose-500 mt-0.5 min-h-[32px]" onClick={() => onWithdraw(row.id)} data-testid="kid-request-withdraw">{ct("kid.requests.withdraw", "Take it back")}</button>
        )}
      </div>
      <span className={`flex-shrink-0 whitespace-nowrap truncate rounded-lg px-2.5 py-1 text-xs font-bold ${chip.cls}`} style={{ maxWidth: "46%" }} data-testid="kid-request-chip">{chip.text}</span>
    </div>
  );
}

// An ANSWERED request in the timeline (refused, taken back, made a goal), drawn to the same
// pattern as an approved one (Alan, 1 Oct: "make them follow the same visual pattern as
// approved requests"). KidRequestRow above is now the WAITING row only — the pinned band.
//
// What maps to what, against "Alan said yes / 08:53 · Read a short book / +14":
//   line one   the outcome, unnamed ("Not this time") — a no is never attributed (19 Sep)
//   line two   the date and what was asked for
//   right      what was asked, with NO sign and in grey, because nothing moved
//   disc       the same glyph an approval of that kind carries, on a neutral ground
// The parent's note, when there is one, stays a full-width line under the row.
function kidAnsweredProps(row) {
  const kind = ACTIVITY_KIND_STYLE[row.requestType] || { icon: "ic-recent" };
  const icon = kind.icon === "coin" ? <CoinIcon className="w-4 h-4 inline-block grayscale opacity-70" />
    : kind.icon === "vault" ? <VaultIcon className="w-4 h-4 inline-block grayscale opacity-70" />
    : kind.icon;
  const label = row.state === "no" ? ct("kid.requests.chip_no", "Not this time")
    : row.state === "goal" ? ct("kid.requests.chip_goal", "Made a goal")
    : ct("kid.requests.chip_back", "Taken back");
  const amount = row.pricePence != null ? fmtPence(row.pricePence) : row.tokens > 0 ? fmtNumber(row.tokens) : "";
  return { icon, iconBgClass: "bg-slate-100", iconColorClass: "text-slate-500", label, detail: row.title || undefined, timestamp: fmtActivityStamp(row.at), amount, amountColorClass: "text-slate-400" };
}

function KidAnsweredRow({ row }) {
  return (
    <div data-testid="kid-request-row" data-state={row.state}>
      <ActivityRow {...kidAnsweredProps(row)} />
      {row.note && <div className="ml-9 mb-2 text-xs font-semibold text-slate-600 rounded-lg bg-slate-50 px-2.5 py-2 break-words" data-testid="kid-request-note">{row.note}</div>}
    </div>
  );
}

// Kid Home's Recent Activity card. Collapsed by default to a single most-recent row plus
// a "See more" link — the full paginated ActivityList (unchanged, still used as-is in the
// Rewards tab's Transaction History) only mounts once expanded, so nothing about that
// shared component needed to change for this.

// LB-343 (G1) — the point of the epic, on the child's side. A child seeing "Grandma said yes"
// instead of a nameless approval is the whole reason adults have names at all, so the name
// leads the row and the reason moves to the muted line beneath it.
//
// No avatar: adults have no avatar field and none is being added (Alan, 18 Sep), so this is
// the name alone. Where there is no name to show — an older entry, or a shared device nobody
// had unlocked — the row stays exactly as it was rather than saying "A parent said yes",
// which tells a child nothing and reads worse than the plain reason.
//
// Two cases, both transactions: an approval (it carries the requestId it settled) and money
// given (a credit with no request behind it). A REJECTION never becomes a transaction, so it
// is not reachable from here — see the report.
function kidHistoryLead(t, name) {
  if (!name || !t || t.deviceId === "system") return null;
  // LB-356: an Oinks row is never "gave you N tokens". A sibling's gift already names the
  // sibling in its reason, so it takes no adult lead at all; a grown-up's gift says Oinks.
  if (t.balance === "oinks") return t.kind === "oink_gift_sibling" ? null : ct("kid.history.gave_oinks", "{name} gave you {amount} Oinks", { name, amount: t.amount });
  // A NO IS NEVER ATTRIBUTED ON THE KID SIDE (Alan, 19 Sep). A yes has a person behind it —
  // that is the whole point of the epic, and "Grandma said yes" is worth a child reading. A
  // no does not need a face to blame, and putting one there turns a household decision into a
  // grievance against one parent. So a refused row says "Not this time" and names nobody.
  //
  // The record still carries adultId and adultName: parent history shows who decided, and
  // removing the name here is a rendering rule, not an audit gap. `name` is deliberately
  // unused on this branch.
  if (t.status === "rejected") return ct("kid.history.rejected", "Not this time");
  if (t.requestId) return ct("kid.history.approved", "{name} said yes", { name });
  if (t.type === "credit") return ct("kid.history.gave", "{name} gave you {amount} tokens", { name, amount: t.amount });
  return null;
}

// `adults` rather than the whole state: LB-343 needs one lookup, and kid-mode components
// stay as small as they were (CLAUDE.md — kid mode is reuse-verbatim, and this row is the one
// thing the brief opens up).
export function RecentActivityCard({ transactions, requests = [], adults = [], onWithdraw }) {
  const [expanded, setExpanded] = useState(false);
  // LB-346 option R: the timeline carries the requests that never became a transaction, and
  // the waiting ones sit pinned above it in a band of their own — they are not in date order
  // with the rows beneath, and the band's caption + tint say so.
  const { waiting, timeline } = kidTimeline(transactions, requests, adults);
  // Alan, 1 Oct: an answered request in the timeline is drawn by the SAME row an approval is
  // (ActivityRow), not by the bolder waiting row. Before this a refusal was the most prominent
  // thing in the list: a 15px bold title and a chip beside rows of 12px text. Now it is disc,
  // one line, a muted second line and the amount on the right, like its neighbours.
  const renderItem = (it) => it.kind === "req" ? <KidAnsweredRow key={it.id} row={it.row} /> : renderRow(it.tx);
  const pinned = waiting.length > 0 && (
    <div data-testid="kid-waiting-band" className="bg-amber-50/60">
      <div className="px-4 pt-2 pb-0.5 text-[11px] font-extrabold tracking-wide text-amber-800">{ct("kid.requests.group_waiting", "Waiting for a grown-up")}</div>
      {waiting.map((r) => <KidRequestRow key={r.id} row={r} onWithdraw={onWithdraw} />)}
    </div>
  );
  const renderRow = (t) => {
    const style = activityRowStyle(t);
    // Kid Mode never calls the devices route, so there is no device-owner map here — the name
    // is the one written at the time, or the adult the row names, or nothing.
    const lead = kidHistoryLead(t, attributionName({ adults }, t));
    return (
      <ActivityRow
        key={t.id}
        {...style}
        label={lead || t.reason}
        detail={lead ? t.reason : undefined}
        timestamp={fmtActivityStamp(t.timestamp)}
        status={t.status}
        reversed={t.reversed}
        isReversal={!!t.reversalOf}
      />
    );
  };
  // W3.3 v2: subtle money-family wash (same family as goals and Transaction History,
  // since this card IS the money ledger at a glance); header stays white/neutral.
  return (
    // LB-164: rows on the canvas; the "Recent activity" heading is the section head in HomeTab.
    <div className="kid-rows">
      <div className="ks-title kc-gold" style={{ marginBottom: 0, paddingTop: 6 }}><span className="kc-disc" aria-hidden="true"><Icon id="ic-recent" size={16} style={{ color: "currentColor" }} /></span><span className="kc-title">{ct("kid.home.section_recent", "Recent activity")}</span>{waiting.length > 0 && <span className="ks-meta text-amber-800">{ct("kid.requests.waiting_meta", "{n} waiting", { n: waiting.length })}</span>}</div>
      {pinned}
      {expanded ? (
        <>
          <ActivityList items={timeline} emptyText="No activity yet." renderRow={renderItem} />
          <button className="kid-row w-full text-center text-xs font-bold text-slate-500 justify-center" onClick={() => setExpanded(false)}>{ct("kid.show_less", "Show less")}</button>
        </>
      ) : timeline.length === 0 ? (
        !pinned && <div className="kid-row text-base text-slate-400">{ct("kid.no_activity", "No activity yet.")}</div>
      ) : (
        <>
          {/* Two rows, not one (Alan, 9 Sep): one row says nothing happened; two show a pattern. */}
          {renderItem(timeline[0])}
          {timeline.length > 1 && renderItem(timeline[1])}
          {timeline.length > 2 && <button className="kid-row w-full text-xs font-bold justify-center" style={{ color: "var(--kid-pop)" }} onClick={() => setExpanded(true)}>{ct("kid.recent.see_more", "See more")}</button>}
        </>
      )}
    </div>
  );
}
